import { RAW_EVENTS } from './catalog';
import { LIVE_EVENTS } from './live';
import { store } from './store';
import type { Event } from '../types';
import { eventPath, eventSlug, slugify } from '../../shared/slug.mjs';
import { CITY_ALIASES as CITY_SHORTHAND } from '../../shared/search-helpers.mjs';

/**
 * The catalog API. Data lives in the store (src/data/store.ts): the
 * prerender seeds it with everything below; the browser gets an empty
 * `./live` (vite alias) plus whatever the page embedded, and components
 * declare the slices they need through useEvents/useEvent.
 *
 * Everything here is a function over `store.all()` — never a module-level
 * array — so nothing computes over an empty catalog at import time in the
 * browser. Counts and top-N lists that the chrome needs before any slice has
 * loaded come from src/data/nav.json (generated with the catalog).
 */

// Curated marquee events come first and keep their `featured` flags; live
// SeatGeek events fill out the catalog. The store dedupes by id (first wins).
if (import.meta.env.SSR || LIVE_EVENTS.length) {
  const seen = new Set<string>();
  const merged = [];
  for (const e of [...RAW_EVENTS, ...LIVE_EVENTS]) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    merged.push(e);
  }
  store.seed(merged);
}

/** Local YYYY-MM-DD — never UTC. new Date('YYYY-MM-DD') parses as UTC midnight,
 * which hid all day-of-show events from US visitors (their local date is behind UTC). */
export const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Every known event, soonest first (what the store holds right now). */
export const allEvents = (): Event[] => store.all();

/** Upcoming events, soonest first. Pass a list to filter a subset. */
export const upcoming = (list: Event[] = store.all()): Event[] => {
  const today = localToday();
  return list.filter((e) => e.date >= today);
};

// URLs and names of events: one rule for the build, the edge and the site (shared/slug.mjs)
export { slugify, eventSlug, eventPath };

export const eventById = (id: string) => store.get(id);


// Lookups accept either the display name or its slug (for clean URLs).
export const matchesName = (value: string, nameOrSlug: string) =>
  value.toLowerCase() === nameOrSlug.toLowerCase() || slugify(value) === nameOrSlug.toLowerCase();

export const eventsByArtist = (nameOrSlug: string, list: Event[] = upcoming()) =>
  list.filter((e) => e.artist && matchesName(e.artist, nameOrSlug));

export const eventsByVenue = (nameOrSlug: string, list: Event[] = upcoming()) =>
  list.filter((e) => matchesName(e.venue, nameOrSlug));

export const eventsByTeam = (nameOrSlug: string, list: Event[] = upcoming()) =>
  list.filter((e) => e.teams?.some((t) => matchesName(t, nameOrSlug)));

export const eventsByCity = (nameOrSlug: string, list: Event[] = upcoming()) =>
  list.filter((e) => matchesName(e.city, nameOrSlug));

export const displayNameFor = (
  kind: 'artist' | 'venue' | 'city' | 'team',
  slug: string,
  list: Event[] = store.all()
): string => {
  for (const e of list) {
    if (kind === 'artist' && e.artist && matchesName(e.artist, slug)) return e.artist;
    if (kind === 'venue' && matchesName(e.venue, slug)) return e.venue;
    if (kind === 'city' && matchesName(e.city, slug)) return e.city;
    if (kind === 'team') for (const t of e.teams ?? []) if (matchesName(t, slug)) return t;
  }
  // Nothing loaded yet (browser, before a slice arrives): a readable guess
  // from the slug — "las-vegas" → "Las Vegas". Corrected once data lands.
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const countBy = (list: Event[], get: (e: Event) => string | undefined) => {
  const m = new Map<string, number>();
  for (const e of list) {
    const k = get(e);
    if (k) m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
};

export const citiesOf = (list: Event[] = upcoming()) =>
  countBy(list, (e) => e.city).map(([name, n]) => ({ name, n, slug: slugify(name) }));
export const venuesOf = (list: Event[] = upcoming()) =>
  countBy(list, (e) => e.venue).map(([name, n]) => ({ name, n, slug: slugify(name) }));
export const artistsOf = (list: Event[] = upcoming()) =>
  [...new Set(list.filter((e) => e.artist).map((e) => e.artist!))].sort();
export const teamsOf = (list: Event[] = upcoming()) =>
  [
    ...new Set(list.flatMap((e) => e.teams ?? []).filter((t) => !/TBD|Final|Round|Match|Winner|Runner/i.test(t))),
  ].sort();

// Natural-language friendly search: strips filler words, scores events by how
// many meaningful tokens match across artist/name/city/venue/teams/tournament.
// "two tickets to coldplay at wembley under £150" → tokens [coldplay, wembley].
const STOPWORDS = new Set(
  ('a an and at for from in me my near of on the to with tickets ticket seats seat show shows gig ' +
    'concert match game event events cheap cheapest best good great decent way see two three four ' +
    'under over around about this that next weekend tonight week month together view please want').split(' ')
);

// Common US city shorthand → catalog city names: one table for the site and the edge
// (shared/search-helpers.mjs; functions/api/search.ts reads the same one).
const CITY_ALIASES: Record<string, string> = CITY_SHORTHAND;

/** "tonight" / "today" / "tomorrow" / "this weekend" → a date window. */
const temporalWindow = (q: string): { from: string; to: string } | null => {
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const now = new Date();
  if (/\btonight\b|\btoday\b/.test(q)) return { from: fmt(now), to: fmt(now) };
  if (/\btomorrow\b/.test(q)) {
    const t = new Date(now);
    t.setDate(now.getDate() + 1);
    return { from: fmt(t), to: fmt(t) };
  }
  if (/\bthis weekend\b|\bweekend\b/.test(q)) {
    const start = new Date(now);
    const dow = now.getDay();
    if (dow >= 1 && dow <= 4) start.setDate(now.getDate() + (5 - dow));
    const end = new Date(start);
    end.setDate(start.getDate() + (start.getDay() === 0 ? 0 : 7 - start.getDay()));
    return { from: fmt(start), to: fmt(end) };
  }
  return null;
};

/** Local scorer over a list (the index slice in the browser, everything in
 *  the prerender). /api/search does the real ranking; this is the fallback
 *  and the homepage's live filter. */
export const searchEvents = (query: string, list: Event[] = upcoming()) => {
  const q = query.toLowerCase();
  const window = temporalWindow(q);
  const tokens = q
    .replace(/[£$€]\s*\d+|\d+/g, ' ')
    .split(/[^a-zà-ÿ']+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t))
    .flatMap((t) => (CITY_ALIASES[t] ? CITY_ALIASES[t].split(' ') : [t]));
  // Pure temporal query ("what's on tonight") → best of the window.
  if (!tokens.length && window)
    return list
      .filter((e) => e.date >= window.from && e.date <= window.to)
      .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))
      .slice(0, 24);
  if (!tokens.length) return [];

  const pool = window ? list.filter((e) => e.date >= window.from && e.date <= window.to) : list;

  const scored = pool
    .map((e) => {
      const hay = [e.name, e.artist, e.city, e.venue, e.tournament, e.context, ...(e.teams ?? [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      let score = 0;
      for (const t of tokens) if (hay.includes(t)) score++;
      return { e, score };
    })
    .filter((s) => s.score > 0);

  const max = Math.max(...scored.map((s) => s.score), 0);
  // Prefer events matching the most tokens; break ties by soonest date.
  return scored
    .filter((s) => s.score === max || s.score >= 2)
    .sort((a, b) => b.score - a.score || a.e.date.localeCompare(b.e.date))
    .map((s) => s.e);
};
